import { createSession } from '@evnttz/session'

import { clientEnv } from '@/env.client'

import type { Session, SessionCore } from '@evnttz/session'


/**
 * A abertura dentro do Aplicativo do Evento: a WebView chega em
 * `/auth/refresh-session?token=…` com o access token do app nativo. Lido na
 * criação, antes de qualquer roteador — o caminho não tem prefixo de tenant
 * (o tenant vem do host).
 */
const readNativeHandoffToken = (): string | null => {
  if (typeof window === 'undefined') return null
  if (window.location.pathname.replace(/\/+$/, '') !== '/auth/refresh-session') return null

  return new URLSearchParams(window.location.search).get('token') || null
}

/**
 * A instância única de sessão do gamification.
 *
 * O `client_secret` dos providers mora só na api; o access token vive em
 * memória deste módulo e o refresh token no armazenamento do navegador,
 * rotacionado a cada uso (pacote `@evnttz/session`).
 *
 * É criada no import — e importada no topo do `main.tsx`, antes do roteador —
 * porque é na criação que o `#session_code=` da volta do login social é
 * capturado. E porque o `tokenStore` e o portão de boot precisam ser os mesmos
 * para toda a aplicação: duas instâncias significam dois tokens, duas
 * renovações concorrentes e um logout que não desloga.
 */
const core: SessionCore = createSession({
  baseURL: clientEnv.NEXT_PUBLIC_EVNTTZ_BACKEND_API_BASE_URL,
  // Outra aba entrou com outra conta: recarregar descarta o que está em tela
  // e em cache da conta anterior.
  onUserChange: () => window.location.reload()
})

const nativeHandoffToken = readNativeHandoffToken()

let nativeHandoff: Promise<Session | null> | null = null

/**
 * Na abertura pelo app nativo o boot é a troca do token dele por uma sessão web
 * própria — uma única vez — e não a renovação do token guardado: os dois em
 * paralelo disputariam o armazenamento, e o token entregue de volta à ponte
 * nativa poderia ser de uma família já encerrada. Token recusado resolve em
 * `unauthenticated` (nunca rejeita) e deixa o token guardado onde estava.
 */
const restoreFromNativeHandoff = (): Promise<Session | null> => {
  nativeHandoff ??= core.signInWithToken(nativeHandoffToken as string).catch(() => null)

  return nativeHandoff
}

export const session: SessionCore = nativeHandoffToken
  ? { ...core, restore: restoreFromNativeHandoff, ready: restoreFromNativeHandoff }
  : core

/** Portão de boot: nenhuma requisição autenticada sai antes disto resolver. */
export const sessionReady = () => session.ready()

/**
 * O access token, para quem não passa por uma requisição HTTP comum.
 *
 * Casos legítimos: o ActionCable, porque WebSocket não passa por `fetch`, e a
 * ponte com o app nativo na WebView.
 */
export const getAccessToken = (): string | null => session.tokenStore.get()
